// TanStack Query kalitlari BIR JOYDA e'lon qilinadi.
//
// Sabab: keshni bekor qilish (`invalidateQueries`) kalit prefiksi bo'yicha
// ishlaydi. Kalitlar chaqiruv joylarida qo'lda yozilsa, biri `['subscribers']`
// deb, ikkinchisi `['subscriber-list']` deb yozadi va invalidatsiya jimgina
// ishlamay qoladi — ma'lumot eskirgan holda ekranda qoladi.
//
// `as const` MUHIM: kalit tuple sifatida tiplanadi, ya'ni noto'g'ri
// argument berilsa kompilyatsiyada ushlanadi.

export const queryKeys = {
  me: ['me'] as const,

  /**
   * Foydalanuvchi o'ta oladigan tashkilotlar (`GET /api/auth/tenants`).
   *
   * ⚠️ `me` DAN ALOHIDA: `me` JORIY tashkilotni aytadi va tashkilot
   * almashtirilganda butun kesh tozalanadi (`AuthProvider.switchTenant`),
   * bu ro'yxat esa almashtirishdan KEYIN ham o'sha-o'sha bo'lib qoladi —
   * a'zoliklar to'plami tanlovga bog'liq emas.
   */
  myTenants: ['auth', 'tenants'] as const,

  collectLimits: {
    all: ['collectLimits'] as const,
    mine: ['collectLimits', 'mine'] as const,
    list: ['collectLimits', 'list'] as const,
    cashiers: ['collectLimits', 'cashiers'] as const,
    requests: (state: string | null) => ['collectLimits', 'requests', state] as const,
  },
  contracts: {
    all: ['contracts'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['contracts', 'list', params] as const,
    template: ['contracts', 'template'] as const,
  },
  subscribers: {
    all: ['subscribers'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['subscribers', 'list', params] as const,
    detail: (id: string) => ['subscribers', 'detail', id] as const,
    /** Tanlagich qidiruvi — `list` dan ALOHIDA: u jadval holatiga bog'liq */
    search: (term: string) => ['subscribers', 'search', term] as const,
    grants: (id: string) => ['subscribers', 'grants', id] as const,
    contracts: (id: string) => ['subscribers', 'contracts', id] as const,
    person: (id: string) => ['subscribers', 'person', id] as const,
    formSettings: ['subscribers', 'formSettings'] as const,
    /** Kartochkaning to'liq manzarasi — balans, qoldiq, tezlik, sessiya. */
    overview: (id: string) => ['subscribers', 'overview', id] as const,
    history: (id: string, from: string, to: string) => ['subscribers', 'history', id, from, to] as const,
    activeGrant: (id: string) => ['subscribers', 'activeGrant', id] as const,
    /** Barcha amaldagi grantlar + qaysi biri tezlikni/hajmni boshqaradi */
    grantOverview: (id: string) => ['subscribers', 'grantOverview', id] as const,
    /**
     * Tashkilotning RADIUS realm'i (`reference.ts`).
     *
     * Tenantga bog'liq, abonentga emas — shuning uchun `detail` ostida
     * emas, alohida. Sessiya davomida o'zgarmaydi.
     */
    radiusRealm: ['subscribers', 'radiusRealm'] as const,
    /**
     * Tashkiliy tugun OSTIDAGI abonentlar SONI (`reference.ts`).
     *
     * ⚠️ `list` DAN ALOHIDA: u qatorlarni saqlaydi, bu esa bitta son.
     * Bitta kalitda bo'lsa, ko'chirish modalidagi "nechta abonent
     * ta'sirlanadi" so'rovi abonentlar jadvalining keshini
     * ALMASHTIRIB yuborardi — jadval `size=1` bilan olingan bitta
     * qatorni ko'rsatardi.
     */
    countUnder: (nodeId: string) => ['subscribers', 'countUnder', nodeId] as const,
    deviceTypes: (includeInactive: boolean) => ['subscribers', 'deviceTypes', includeInactive] as const,
  },

  sessions: {
    all: ['sessions'] as const,
    active: ['sessions', 'active'] as const,
    inconsistencies: ['sessions', 'inconsistencies'] as const,
    /**
     * Faqat loginlar to'plami (`reference.ts`).
     *
     * ⚠️ `active` DAN ALOHIDA KALIT bo'lishi SHART: ikkalasi bir xil
     * endpointdan o'qiydi, lekin keshda BOSHQA shakl saqlaydi
     * (`Set<string>` va `ActiveSession[]`). Bitta kalitni baham ko'rishsa,
     * qaysi so'rov oxirgi tugagani keshdagi tipni belgilardi va ikkinchi
     * iste'molchi kutilmagan shaklni olardi.
     */
    onlineLogins: ['sessions', 'onlineLogins'] as const,
    /** Bitta abonentning faol sessiyalari — kartochkada ko'rsatiladi */
    activeOf: (username: string) => ['sessions', 'activeOf', username] as const,
    history: ['sessions', 'history'] as const,
    /**
     * Autentifikatsiya urinishlari (`radpostauth`) — `history` DAN ALOHIDA.
     *
     * Ikkalasi ham "o'tmish" haqida, lekin BOSHQA savolga javob beradi:
     * tarix — kim ULANDI va qancha trafik sarfladi, bu esa kim ULANA
     * OLMADI. Ro'yxatdagi qatorlar ham boshqa jadvaldan
     * (`radacct` / `radpostauth`) va shakli boshqa.
     */
    authLog: ['sessions', 'authLog'] as const,
    /** Bosh sahifadagi qisqa ko'rinish — to'liq jurnaldan ALOHIDA kesh yozuvi */
    recentAuth: ['sessions', 'authLog', 'recent'] as const,
    stats: ['sessions', 'stats'] as const,
    stale: ['sessions', 'stale'] as const,
    reconciliation: ['sessions', 'reconciliation'] as const,
  },

  /**
   * Grantlar — bosh sahifadagi "e'tibor talab qiladi" ro'yxati.
   *
   * ⚠️ `subscribers.grants(id)` DAN ALOHIDA: u BITTA abonentning
   * grantlari, bu esa BUTUN doira bo'yicha tugash arafasidagilar.
   * Bitta prefiksda bo'lsa, abonent kartochkasidagi yangilanish bosh
   * sahifadagi ro'yxatni ham bekor qilardi va teskarisi.
   */
  grants: {
    expiring: ['grants', 'expiring'] as const,
    /**
     * Berilgan paketlar RO'YXATI (`GET /api/grants`, `GrantsPage`).
     *
     * ⚠️ `expiring` VA `subscribers.grants(id)` DAN ALOHIDA: birinchisi
     * bosh sahifadagi qisqa ro'yxat, ikkinchisi bitta abonentning
     * sahifalanmagan grantlari, bu esa filtrlangan SAHIFA
     * (`PageResult`). Uchtasi bir kalitni baham ko'rsa keshda BOSHQA
     * shakl saqlaydigan iste'molchilar bir-birining javobini olardi.
     * Filtr va sahifa raqamini kalitga `DataTable` o'zi qo'shadi.
     */
    list: ['grants', 'list'] as const,
  },

  catalog: {
    all: ['catalog'] as const,
    deniedPlans: (branchId: string | null) => ['catalog', 'deniedPlans', branchId] as const,
    plans: ['catalog', 'plans'] as const,
    /** id → nom xaritasi (`reference.ts`). `plans` dan alohida — yuqoridagi izoh. */
    planIndex: ['catalog', 'planIndex'] as const,
    plan: (id: string) => ['catalog', 'plan', id] as const,
    planBranchMap: ['catalog', 'planBranchMap'] as const,
    planAccess: (id: string) => ['catalog', 'planAccess', id] as const,
    policies: ['catalog', 'policies'] as const,
    /** id → nom/tezlik xaritasi (`reference.ts`). `policies` dan alohida — `planIndex` bilan bir xil sabab. */
    policyIndex: ['catalog', 'policyIndex'] as const,
    policy: (id: string) => ['catalog', 'policy', id] as const,
    /** Paketga ANIQ qo'yilgan reseller narxlari (meros olinganlar bu yerda yo'q) */
    planPrices: (planId: string) => ['catalog', 'planPrices', planId] as const,
    /*
      AMALDAGI narx — meros hisobga olingan holda.
      ⚠️ Reseller KALITGA kiradi: har biriga alohida so'rov ketadi va
      ular bir-birining natijasini almashtirib yubormasligi kerak.
    */
    effectivePrice: (planId: string, resellerId: string) =>
      ['catalog', 'effectivePrice', planId, resellerId] as const,
    /**
     * Atributlar to'plami yetkazish usuliga BOG'LIQ (HOTSPOT'da qo'shimcha
     * `Session-Timeout`/`Mikrotik-Total-Limit` chiqadi), shuning uchun usul
     * kesh kalitining bir qismi — aks holda usul almashtirilganda eski
     * ro'yxat ekranda qolib ketardi.
     */
    policyAttributes: (id: string, deliveryMethod: string) =>
      ['catalog', 'policy', id, 'radius', deliveryMethod] as const,
  },

  /**
   * Tashkiliy daraxt (`platform.org_node`) — QARORLAR.md §19.
   *
   * ⚠️ `agents` DAN ALOHIDA PREFIKS, garchi bugun ikkala endpoint ham
   * BITTA jadvalga borsa ham (`agent.party_node` — `platform.org_node`
   * ustidagi moslik VIEW i). Sabab shakl farqida: `agents` javobida
   * `kind` YOPIQ ro'yxat (`AGENT`/`RESELLER`) va `settlementMode`
   * majburiy, bu yerda esa `kind` — administrator kengaytiradigan tur
   * kodi va `settlementMode` `null` bo'lishi mumkin. Bitta prefiksni
   * baham ko'rishsa, qaysi so'rov oxirgi tugagani keshdagi tipni
   * belgilardi va ikkinchi iste'molchi kutilmagan shaklni olardi
   * (`sessions.onlineLogins` bilan aynan bir xil tuzoq).
   *
   * ⚠️ `types` — `tree` DAN ALOHIDA. Tur ta'rifi o'zgarganda daraxtning
   * O'ZI o'zgarmaydi (tugunlar joyida qoladi), lekin panel ularni
   * qanday CHIZISHI o'zgaradi. Teskarisi ham to'g'ri: tugun
   * ko'chirilganda turlar ro'yxati eskirmaydi — faqat `usedBy` sanog'i,
   * u esa tugun YARATILGANDA/turi o'zgarganda bekor qilinadi.
   */
  org: {
    all: ['org'] as const,
    tree: ['org', 'tree'] as const,
    node: (id: string) => ['org', 'node', id] as const,
    subtree: (id: string) => ['org', 'subtree', id] as const,
    types: ['org', 'types'] as const,
  },

  agents: {
    all: ['agents'] as const,
    /** Ildiz agentlar. ⚠️ Agent uchun bu FAQAT o'z tugunini qaytaradi (doira OWN). */
    roots: ['agents', 'roots'] as const,
    tree: ['agents', 'tree'] as const,
    list: (q: string) => ['agents', 'list', q] as const,
    detail: (id: string) => ['agents', 'detail', id] as const,
    children: (id: string) => ['agents', 'children', id] as const,
    /**
     * Tugun va butun ostidagilar (`GET /{id}/subtree`).
     *
     * ⚠️ `children` DAN AYRIM: bittasi FAQAT birinchi qavat, bu esa
     * butun shox. Ko'chirish oynasi «nechta tugun ko'chadi» degan
     * savolga aynan shu javobga tayanadi va bir qavatni butun shox
     * deb ko'rsatish raqamni HAQIQIYDAN kichik chiqarardi.
     */
    subtree: (id: string) => ['agents', 'subtree', id] as const,
    balance: (id: string) => ['agents', 'balance', id] as const,
    outstanding: (id: string) => ['agents', 'outstanding', id] as const,
    commissionRules: (id: string) => ['agents', 'commissionRules', id] as const,
    /**
     * «Bu summada qancha komissiya» — SERVER hisoblagan raqam.
     *
     * ⚠️ Kalitda SUMMA ham bor: har summa boshqa javob beradi va ular
     * bitta kalitni baham ko'rsa, ikkinchi summa birinchisining
     * raqamini bir lahza ko'rsatardi — pul ekranida bu jimgina
     * yolg'on.
     *
     * ⚠️ `commissionRules` prefiksidan TASHQARIDA: qoida
     * o'zgarganda ro'yxat bekor qilinadi, oldindan ko'rish esa
     * operator summa yozganda o'zi qayta so'raladi.
     */
    commissionPreview: (id: string, amountMinor: number, currency: string) =>
      ['agents', 'commissionPreview', id, amountMinor, currency] as const,
    /**
     * Oylik suiiste'mol chegaralari va joriy oyda ishlatilgani.
     *
     * ⚠️ `balance` bilan BIRLASHTIRILMADI: balans har depozitdan keyin
     * bekor qilinadi va ular bir kalitda tursa limitlar ham har pul
     * amalidan keyin qayta so'ralardi — holbuki ular faqat qo'lda
     * o'zgaradi va oy boshida nolga tushadi.
     */
    limits: (id: string) => ['agents', 'limits', id] as const,
    /**
     * Kunlik va oylik PUL limiti (`GET /{id}/fund-limits`).
     *
     * ⚠️ `limits` DAN AJRATILGAN va bu ataylab: u amal SONINI sanaydi
     * (dona, resellerda), bu esa PULNI (minor birlik, agentda). Ikkalasi
     * boshqa endpointda va boshqa tezlikda o'zgaradi — pul limitidagi
     * "sarflangani" har to'ldirishdan keyin yangilanadi, amal soni esa
     * faqat abonent tahrirlanganda.
     */
    fundLimits: (id: string) => ['agents', 'fundLimits', id] as const,
  },

  /**
   * NAS (RADIUS klientlari).
   *
   * `all` — PREFIKS: `DataTable` jadval holatini (sahifa, o'lcham) shu
   * kalitga o'zi qo'shadi, ya'ni `invalidateQueries({queryKey: nas.all})`
   * qaysi sahifada turgan bo'lsa ham ro'yxatni yangilaydi. Yaratish,
   * tahrirlash, sir almashtirish va o'chirish — hammasi shu bittasini
   * bekor qiladi.
   */
  nas: {
    all: ['nas'] as const,
    /**
     * Qurilmalarning TIRIKLIK belgisi (`GET /api/nas/health`).
     *
     * ⚠️ `all` PREFIKSIDAN TASHQARIDA — ataylab. Prefiks ostida bo'lsa
     * har tahrirlash/o'chirish (`invalidateQueries({queryKey: nas.all})`)
     * holatni ham bekor qilardi, holat esa `radacct` dan keladi va
     * sozlama o'zgarishidan mutlaqo mustaqil. Bundan ham muhimi teskarisi:
     * holat har 30 soniyada o'zi yangilanadi, va u ro'yxat bilan bitta
     * prefiksda bo'lsa jadval ham har 30 soniyada qayta so'ralardi.
     */
    health: ['nas-health'] as const,
  },

  /** Center, VISP va routerlar biriktirmasi (`/api/network/*`); `all` — prefiks. */
  network: {
    all: ['network'] as const,
    centers: ['network', 'centers'] as const,
    visps: ['network', 'visps'] as const,
    /** Tor shakl (`reference.ts`) — `visps` bilan bitta kalitda bo'lsa kesh shakli aralashardi */
    vispOptions: ['network', 'vispOptions'] as const,
    routers: ['network', 'routers'] as const,
    userAssignment: (userId: string) => ['network', 'userAssignment', userId] as const,
  },

  /**
   * Joylashuvlar reyestri (`site`).
   *
   * `all` — PREFIKS va u ATAYLAB `index`/`regions` ni ham qamrab oladi:
   * joylashuv nomi o'zgarsa yoki yopilsa, faqat ro'yxat emas, NAS va jihoz
   * jadvallarida ishlatiladigan NOM XARITASI ham eskiradi. Bitta
   * `invalidateQueries({queryKey: sites.all})` uchalasini ham yangilaydi.
   */
  sites: {
    all: ['sites'] as const,
    /** id → yozuv xaritasi (`useSiteIndex`) — ro'yxatdan ALOHIDA: shakli boshqa */
    index: ['sites', 'index'] as const,
    /** Viloyat nomlari — `AutoComplete` uchun, hisobotdagi guruhlash kaliti */
    regions: ['sites', 'regions'] as const,
    detail: (id: string) => ['sites', 'detail', id] as const,
  },

  /**
   * Viloyat/tuman ma'lumotnomasi.
   *
   * `all` — PREFIKS: viloyat nomi o'zgarsa yoki nofaol qilinsa, ro'yxat ham,
   * manzil shaklidagi tanlov ro'yxatlari ham eskiradi.
   */
  /**
   * Umumiy kontakt reestri (`platform.contact`, ADR-031).
   *
   * ⚠️ Kalit EGA bo'yicha, kontakt `id` si bo'yicha EMAS: abonent
   * kartochkasi kontaktning `id` sini bilmaydi va uni faqat shu so'rov
   * orqali topadi.
   */
  contacts: {
    all: ['contacts'] as const,
    byOwner: (ownerType: string, ownerId: string) =>
      ['contacts', 'byOwner', ownerType, ownerId] as const,
  },

  geo: {
    all: ['geo'] as const,
    regions: ['geo', 'regions'] as const,
    districts: ['geo', 'districts'] as const,
    regionOptions: (activeOnly: boolean) => ['geo', 'regionOptions', activeOnly] as const,
    districtOptions: (regionId: string | null, activeOnly: boolean) =>
      ['geo', 'districtOptions', regionId, activeOnly] as const,
  },

  /**
   * Davomat — yuzni tanish terminallari.
   *
   * `all` — PREFIKS: yaratish, tahrirlash, o'chirish, probe va eshik
   * ochish — hammasi shu bittasini bekor qiladi. Probe qurilmaning
   * `status`/`lastSeenAt`/`clockSkewSeconds` ustunlarini SERVERDA
   * yangilaydi, ya'ni undan keyin ro'yxat ham eskiradi — buni unutish
   * "tekshirdim, holat o'zgarmadi" degan chalkashlikka olib kelardi.
   *
   * ⚠️ Bitta yozuv uchun alohida `detail` kaliti YO'Q: qurilmaning ham,
   * xodimning ham kartochka sahifasi yo'q — tahrirlash modalda va u
   * ro'yxatdagi yozuvni ishlatadi. Kalit qo'shish bir xil ma'lumotni
   * ikki joyda saqlashga va ular ajralib ketishiga olib kelardi.
   *
   * ⚠️ Xodimlar, hodisalar va hisobot ham SHU PREFIKS ostida. Ya'ni
   * xodimni tahrirlash yoki qurilmaga yuborish hisobotni ham bekor
   * qiladi. Bu ortiqcha ko'rinishi mumkin, lekin teskarisi yomonroq:
   * xodim nofaol qilinganda u qurilmalardan olib tashlanadi va
   * hisobotdagi "ichkarida" ro'yxati eskiradi — buni unutish ekranda
   * ziddiyat qoldirardi.
   */
  attendance: {
    all: ['attendance'] as const,
    devices: ['attendance', 'devices'] as const,
    /**
     * Filtr TANLAGICHLARI uchun qisqa ro'yxatlar — `devices`/`employees`
     * dan ALOHIDA kalitlar.
     *
     * ⚠️ Bir xil endpointdan o'qisa ham, keshda BOSHQA shakl saqlaydi
     * (`{value,label}[]`, to'liq yozuv emas) — `sessions.onlineLogins`
     * bilan aynan bir xil holat va bir xil sabab: bitta kalitni baham
     * ko'rishsa, qaysi so'rov oxirgi tugagani keshdagi tipni belgilardi.
     */
    deviceOptions: ['attendance', 'deviceOptions'] as const,
    employeeOptions: ['attendance', 'employeeOptions'] as const,
    employees: ['attendance', 'employees'] as const,
    /** Filtr obyekti kalitga kiradi — u o'zgarsa YANGI so'rov (`AuditPage` andozasi) */
    events: (filter: Readonly<Record<string, unknown>>) =>
      ['attendance', 'events', filter] as const,
    /**
     * Hisobot.
     *
     * ⚠️ Sana/oy KALITGA kiradi. Usiz kun almashtirilganda eski kunning
     * jadvali ekranda qolardi — va bu jimgina bo'lardi, chunki jadval
     * shakli bir xil.
     *
     * ⚠️ Oylikda `employeeId` HAM kalitda. U hisobotning SHAKLINI
     * o'zgartiradi: berilmasa qatorlar butun tashkilotning kunlari,
     * berilsa o'sha xodimning kunlari. Kalitsiz xodim almashtirilganda
     * oldingi javob ekranda qolardi — va ustunlar mos kelgani uchun buni
     * sezish deyarli imkonsiz bo'lardi.
     *
     * ⚠️ `present` va `lastSeen` PARAMETRSIZ: ular "hozir" haqidagi
     * savol, davr kesimi emas. Kalitga sana qo'shilsa har kun
     * almashtirilganda bir xil javob qayta so'ralardi.
     */
    report: {
      daily: (date: string) => ['attendance', 'report', 'daily', date] as const,
      monthly: (month: string, employeeId: string | null) =>
        ['attendance', 'report', 'monthly', month, employeeId] as const,
      present: ['attendance', 'report', 'present'] as const,
      lastSeen: ['attendance', 'report', 'lastSeen'] as const,
    },
  },

  /**
   * Kadrlar (`xsib-hr`) — QARORLAR.md §20.5, §20.9-E.
   *
   * ⚠️ `attendance` DAN ALOHIDA PREFIKS va bu ataylab. Ikkala modul ham
   * "xodim" haqida, lekin §20.6 ularni aniq ajratgan: `hr.person` —
   * OBRO'LI MANBA, `attendance.employee.full_name` — KESH. Bitta
   * prefiksni baham ko'rishsa, davomat sahifasidagi yangilanish kadr
   * kartochkasini ham bekor qilardi va teskarisi — ya'ni panel ikki
   * reyestrni bitta narsadek ko'rsatib, aynan §20.6 ogohlantirgan
   * chalkashlikni yaratardi.
   *
   * ⚠️ `history` — `person(id)` DAN ALOHIDA. Kadr yozuvini tahrirlash
   * (telefon, email) tarixni O'ZGARTIRMAYDI: tarix o'zgarmas va uni
   * faqat YANGI tayinlash uzaytiradi. Bitta kalitda bo'lsa har telefon
   * tuzatishi butun tarixni qayta so'rardi.
   *
   * ⚠️ `jobTitles` KALITDA `activeOnly` BOR: tayinlash oynasi faqat
   * faollarni, katalog sahifasi hammasini so'raydi va ular BOSHQA
   * ro'yxat. Kalitsiz modal ochilgandan keyin katalog sahifasi
   * qisqargan ro'yxatni ko'rsatardi — jimgina.
   */
  hr: {
    all: ['hr'] as const,
    persons: ['hr', 'persons'] as const,
    person: (id: string) => ['hr', 'person', id] as const,
    history: (personId: string) => ['hr', 'history', personId] as const,
    attachmentUrl: (personId: string, fileId: string) =>
      ['hr', 'attachmentUrl', personId, fileId] as const,
    jobTitles: (activeOnly: boolean) => ['hr', 'jobTitles', activeOnly] as const,

    /**
     * Ish haqi va ta'til — HAMMASI `hr` prefiksi ostida.
     *
     * ⚠️ Prefiks BIRGA bo'lishi ATAYLAB: maosh qismini to'xtatish
     * vedomostning keyingi yig'ilishiga ta'sir qiladi, ta'tilni
     * tasdiqlash esa balansni o'zgartiradi. Ular ayri prefikslarda
     * bo'lsa, bitta amaldan keyin ikkinchi ekran ESKIRGAN son bilan
     * qolardi — va bu son PUL bo'lardi.
     */
    /**
     * Tashkilot bo'ylab maosh ro'yxati — xodim kartochkasidagidan
     * ALOHIDA kalit.
     *
     * ⚠️ `salary(personId)` bilan birlashtirilmaydi: bu boshqa
     * endpoint (`GET /api/hr/salaries`), boshqa shakl (sahifa) va
     * boshqa filtr. Bitta kalitga qo'yilsa, bitta xodim kartochkasi
     * ro'yxatning keshini bosib ketardi.
     */
    salaries: ['hr', 'salaries'] as const,
    /*
     * ⚠️ `personIndex` shu yerda edi — maosh ro'yxati ismni alohida
     * so'ragan paytdagi kalit. 2026-08-14 da o'sha yamoq bilan birga
     * olib tashlandi: ism endi `PayItemView.fullName` da keladi va
     * ro'yxat uchun IKKINCHI kesh kerak emas.
     */
    salary: (personId: string) => ['hr', 'salary', personId] as const,
    salaryHistory: (personId: string) => ['hr', 'salaryHistory', personId] as const,
    payComponents: (activeOnly: boolean) => ['hr', 'payComponents', activeOnly] as const,
    payEvents: (personId: string) => ['hr', 'payEvents', personId] as const,

    payrollRuns: ['hr', 'payrollRuns'] as const,
    payrollRun: (id: string) => ['hr', 'payrollRun', id] as const,
    payrollLines: (runId: string) => ['hr', 'payrollLines', runId] as const,
    /** ⚠️ Vedomost satrlaridan ALOHIDA: bu boshqa endpoint va boshqa doira (`PERSON`) */
    payslips: (personId: string) => ['hr', 'payslips', personId] as const,

    leaveTypes: (activeOnly: boolean) => ['hr', 'leaveTypes', activeOnly] as const,
    leaveRequests: ['hr', 'leaveRequests'] as const,
    /** ⚠️ Yil KALITDA — aks holda yilni almashtirganda eski balans qolardi */
    leaveBalance: (personId: string, year: number) =>
      ['hr', 'leaveBalance', personId, year] as const,

    bankAccounts: (personId: string) => ['hr', 'bankAccounts', personId] as const,

    ratingCriteria: (activeOnly: boolean) => ['hr', 'ratingCriteria', activeOnly] as const,
    ratings: (personId: string, page: number) => ['hr', 'ratings', personId, page] as const,
    ratingTrend: (personId: string, from: string, to: string) =>
      ['hr', 'ratingTrend', personId, from, to] as const,

    /**
     * Kadrlar oqimi hisoboti.
     *
     * ⚠️ ORALIQ KALITDA. Usiz oy almashtirilganda ekran OLDINGI oyning
     * sonlarini ko'rsatib turardi va farq faqat qayta yuklashda
     * chiqardi — hisobotda bu "mart oqimi 33%" degan YOLG'ON javob
     * bo'lardi (`leaveBalance` da aynan shu sabab bilan yil kalitda).
     */
    turnover: (from: string, to: string) => ['hr', 'turnover', from, to] as const,
  },

  /** Inventar — jihozlar. `equipment` prefiksi ostida filtr obyekti qo'shiladi. */
  inventory: {
    all: ['inventory'] as const,
    equipment: ['inventory', 'equipment'] as const,
    equipmentDetail: (id: string) => ['inventory', 'equipment', 'detail', id] as const,
    /**
     * Jihoz EGASINI tanlash uchun ro'yxatlar (`features/inventory/ownerApi.ts`).
     *
     * ⚠️ `subscribers`/`agents`/`venues` PREFIKSLARIDAN TASHQARIDA, garchi
     * ma'lumot o'sha endpointlardan kelsa ham. Sabab shaklda: bu yerda keshda
     * `{id,label,hint}[]` saqlanadi, o'sha prefikslarda esa to'liq yozuvlar —
     * `sessions.onlineLogins` bilan aynan bir xil tuzoq (bitta kalitni baham
     * ko'rishsa, qaysi so'rov oxirgi tugagani keshdagi TIPNI belgilardi).
     *
     * ⚠️ Tur ham, qidiruv so'zi ham KALITDA: tur almashtirilganda oldingi
     * turning ro'yxati tanlagichda qolib ketardi va operator abonentlar
     * ro'yxatidan "agent" tanlagandek bo'lardi.
     */
    ownerSearch: (ownerType: string, q: string) =>
      ['inventory', 'ownerSearch', ownerType, q] as const,
    /** Bitta eganing nomi — `ownerSearch` dan ALOHIDA: bu bitta yozuv, u ro'yxat */
    owner: (ownerType: string, ownerId: string) =>
      ['inventory', 'owner', ownerType, ownerId] as const,
  },

  /**
   * Ombor va zaxira (`xsib-inventory` `stock` moduli).
   *
   * ⚠️ `inventory` PREFIKSIDAN TASHQARIDA, garchi backendda bitta
   * servis bo'lsa ham. Sabab modelda: `equipment` — FIZIK NUSXA (seriya
   * raqami bilan), `stock` — MIQDORLI mol turi (`StockItem` javadoc'i:
   * "500 metr kabelning seriya raqami yo'q"). Bitta prefiksda bo'lsa
   * jihozni biriktirish qoldiqlar jadvalini ham bekor qilardi — ular
   * esa bir-biriga umuman ta'sir qilmaydi.
   *
   * ⚠️ `balances` va `valuation` ALOHIDA: qiymat so'ralganda
   * HISOBLANADI (`StockValuation` javadoc'i) va u og'irroq so'rov.
   * Bitta kalitda bo'lsa har qoldiq ko'rinishida ham hisoblanardi.
   * Lekin HARAKAT ikkalasini ham eskirtiradi — shuning uchun amaldan
   * keyin `stock.all` bekor qilinadi.
   */
  stock: {
    all: ['stock'] as const,
    /** BUTUN katalog — mol tanlagichlari uchun (`fetchStockItems`) */
    items: (activeOnly: boolean) => ['stock', 'items', activeOnly] as const,
    /**
     * Katalog JADVALI (`StockItemsPage`) — `DataTable` kalitga jadval
     * holatini o'zi qo'shadi, `activeOnly` esa o'sha holatdagi filtrdan
     * chiqadi. Tanlagichlar kalitidan ALOHIDA: shakli boshqa (bir sahifa).
     */
    itemPage: ['stock', 'items', 'page'] as const,
    warehouses: ['stock', 'warehouses'] as const,
    balances: (nodeId: string | null, itemId: string | null, belowMinOnly: boolean) =>
      ['stock', 'balances', nodeId, itemId, belowMinOnly] as const,
    movements: ['stock', 'movements'] as const,
    valuation: (nodeId: string | null) => ['stock', 'valuation', nodeId] as const,
    /**
     * Ikkinchi imzo kutayotgan so'rovlar (`V202608151800`).
     *
     * ⚠️ `stock` PREFIKSI ICHIDA va bu ataylab: tasdiqlash HARAKAT
     * yozadi, ya'ni qoldiqni ham, jurnalni ham, zaxira qiymatini ham
     * eskirtiradi. `stock.all` bekor qilinganda ro'yxat ham yangilanadi
     * — teskarisi ham kerak: chegaradan oshgan yangi amal so'rov
     * yaratadi va u shu ro'yxatda darhol ko'rinishi kerak.
     */
    approvals: (nodeId: string | null, pendingOnly: boolean) =>
      ['stock', 'approvals', nodeId, pendingOnly] as const,
    /** Tashkilot chegarasi — bitta qator, filtrsiz */
    policy: ['stock', 'policy'] as const,
    /** Ikki bosqichli ko'chirish: jo'natilgan va qabul qilingan */
    transfers: ['stock', 'transfers'] as const,
  },

  /**
   * Tenant sozlamalari (`GET /api/tenant-settings`).
   *
   * `me` DAN ALOHIDA kalit: ikkalasi ham tenant haqida, lekin `me` sessiya
   * davomida o'zgarmaydi (`staleTime: Infinity`), sozlamalar esa aynan shu
   * sahifadan o'zgartiriladi. Bitta kalitda bo'lsa saqlashdan keyin
   * `me` ni bekor qilish sozlamalarni ham qayta so'rashga majburlardi va
   * teskarisi.
   */
  tenantSettings: ['tenantSettings'] as const,
  tenantMenu: ['tenantMenu'] as const,

  /** Kompaniya rekvizitlari, logotip va kartadagi yo'riqnoma (`/api/tenant-settings/branding`) */
  tenantBranding: ['tenantBranding'] as const,

  /**
   * Tashkilotlar REYESTRI (`GET /api/tenants`) — platforma egasi uchun.
   *
   * ⚠️ `myTenants` (`['auth','tenants']`) BILAN CHALKASHTIRMANG. Ular
   * boshqa savolga javob beradi va shakli ham boshqa:
   *   `myTenants`  — men QAYSI tashkilotlarga o'ta olaman (a'zoliklarim,
   *                  faqat FAOL bo'lganlari)
   *   `tenants`    — platformada QANDAY tashkilotlar bor (hammasi,
   *                  to'xtatilgani ham, a'zolikdan qat'i nazar)
   * Bitta kalitni baham ko'rishsa, tanlagich to'xtatilgan tashkilotni
   * ham ko'rsatib qo'yardi.
   */
  tenants: {
    all: ['tenants'] as const,
    detail: (id: string) => ['tenants', 'detail', id] as const,
  },

  identity: {
    /**
     * ⚠️ IKKI XIL ISTE'MOLCHI, BITTA PREFIKS va bu ataylab.
     *
     * `UsersPage` jadvali `DataTable` orqali so'raydi — u kalitga jadval
     * holatini QO'SHADI (`['identity','users',{page,…}]`). Aktyorlar
     * ma'lumotnomasi esa BUTUN ro'yxatni oladi va kalit shu yerda,
     * qo'shimchasiz qoladi. Ular boshqa-boshqa yozuv, lekin bitta
     * prefiks ostida: xodim o'zgarganda bitta `invalidateQueries`
     * ikkalasini ham yangilaydi.
     */
    users: ['identity', 'users'] as const,
    /** Ro'yxat yonidagi kartochka — `GET /api/users/{id}` (ro'yxat sahifalangan) */
    user: (id: string) => ['identity', 'user', id] as const,
    roles: ['identity', 'roles'] as const,
    /** Huquqlar katalogi (`GET /api/roles/permissions`) — `roles` dan ALOHIDA: shakli boshqa */
    permissionCatalog: ['identity', 'permissionCatalog'] as const,
    /** `SUBTREE`/`VENUE` doirasi uchun nishon ro'yxati — faqat rol berish modalida so'raladi */
    scopeTargets: (kind: string) => ['identity', 'scopeTargets', kind] as const,
  },

  audit: {
    /** Sana oralig'i kalitga kiradi — u o'zgarsa YANGI so'rov, eskisi emas */
    list: (from: string, to: string) => ['audit', 'list', from, to] as const,
    recent: ['audit', 'recent'] as const,
  },

  /**
   * Fon vazifalari (`xsib-platform` `job` moduli) — QARORLAR.md §20.1.
   *
   * ⚠️ `audit` DAN ALOHIDA PREFIKS, garchi ikkalasi ham `platform`
   * sxemasida va ikkalasi ham "o'tmish" haqida bo'lsa ham. Ular BOSHQA
   * savolga javob beradi: audit — ODAM nima qildi, bu esa MEXANIZM
   * yurdimi. Bitta prefiksni baham ko'rishsa, reestrning har 60
   * soniyalik avtomatik yangilanishi audit jurnalini ham bekor
   * qilardi va operator ochib turgan jadval o'z-o'zidan qayta
   * so'ralardi.
   *
   * ⚠️ `registry` va `history` ALOHIDA. Reestr — har jobga bitta qator
   * va u o'smaydi (sahifalash yo'q), tarix esa sahifalanadi va keshda
   * BUTUNLAY boshqa shakl saqlaydi (`sessions.onlineLogins` bilan bir
   * xil tuzoq: bitta kalitda qaysi so'rov oxirgi tugagani keshdagi
   * tipni belgilardi).
   *
   * ⚠️ Tarix kalitida SANA ORALIG'I ham bor va u ixtiyoriy emas:
   * backend `from`/`to` siz so'rovni umuman rad etadi (`JobRunQuery`),
   * ya'ni oraliq so'rovning ajralmas qismi. Usiz oraliq
   * almashtirilganda eski davr jadvali ekranda qolib ketardi.
   */
  jobs: {
    all: ['jobs'] as const,
    registry: ['jobs', 'registry'] as const,
    /**
     * Ogohlantirish qorovulining holati.
     *
     * ⚠️ `registry` dan ALOHIDA kalit, garchi ikkalasi ham bitta
     * sahifada bo'lsa ham. Ular boshqa savolga javob beradi ("job
     * qanday" va "buni kimdir ko'rdimi") va bittasi yiqilganda
     * ikkinchisi ekranda QOLISHI kerak — birlashtirilgan kalitda
     * qorovulning yiqilishi reestrni ham o'chirib yuborardi.
     */
    alerts: ['jobs', 'alerts'] as const,
    history: (
      service: string | null,
      job: string | null,
      outcome: string | null,
      from: string,
      to: string,
    ) => ['jobs', 'history', service, job, outcome, from, to] as const,
  },

  reports: {
    /**
     * ⚠️ PARAMETRSIZ kalit — `GET /api/reports/dashboard` sana oralig'ini
     * UMUMAN olmaydi: "bugun" ni server tenant zonasida o'zi aniqlaydi
     * (`reportsApi.ts`). Ilgari bu kalitda brauzer kunining ISO chegaralari
     * turardi va Bosh sahifa bilan Hisobotlar sahifasi bir xil javobni ikki
     * marta so'rardi — endi ikkalasi bitta kesh yozuvini bo'lishadi.
     */
    dashboard: ['reports', 'dashboard'] as const,
    /** Sana oynasi va qadam kalitga kiradi — o'zgarsa YANGI so'rov */
    traffic: (from: string, to: string, granularity: string) =>
      ['reports', 'traffic', from, to, granularity] as const,
    /** Bitta abonent kesimi — `subscriberId` ham kalitda, aks holda kartochkalar bir-birining javobini ko'rsatardi */
    subscriberTraffic: (subscriberId: string, from: string, to: string, granularity: string) =>
      ['reports', 'traffic', 'subscriber', subscriberId, from, to, granularity] as const,
    revenue: (from: string, to: string, granularity: string) =>
      ['reports', 'revenue', from, to, granularity] as const,
    topConsumers: (from: string, to: string, limit: number) =>
      ['reports', 'topConsumers', from, to, limit] as const,
    agents: (from: string, to: string, limit: number) =>
      ['reports', 'agents', from, to, limit] as const,
    /**
     * ⚠️ PARAMETRSIZ — `GET /api/reports/devices` sana oynasini OLMAYDI:
     * u joriy holatni sanaydi, davr ichidagi iste'molni emas
     * (`reportsApi.fetchDeviceReport`). Kalitga davr qo'shilsa, davr
     * almashganda bir xil javob qayta-qayta so'ralardi.
     */
    devices: ['reports', 'devices'] as const,
    /**
     * Daromad kesimlarda (`/api/reports/revenue/by/{dimension}`).
     * ⚠️ KESIM KALITDA: `COST_CENTER` va `PLAN` javoblari butunlay
     * boshqa qatorlar — kalitsiz kesim almashganda ekranda eski
     * jadval turardi.
     */
    revenueBy: (dimension: string, from: string, to: string) =>
      ['reports', 'revenueBy', dimension, from, to] as const,
    /** Abonent bazasining harakati (`/api/reports/subscriber-movement`) */
    subscriberMovement: (from: string, to: string, granularity: string) =>
      ['reports', 'subscriberMovement', from, to, granularity] as const,
  },

  /**
   * To'lovlar — `payments.psp_transaction` (PSP kanali orqali kelganlar).
   *
   * ⚠️ `ledger` dan ALOHIDA kalit, garchi ikkalasi ham pul haqida bo'lsa
   * ham: bekor qilish `ledger` da teskari yozuv qoldiradi, ya'ni ikkala
   * ro'yxat ham eskiradi. Shuning uchun `PaymentsPage` IKKALA prefiksni
   * ham bekor qiladi — bittasini unutish "bekor qildim, lekin jurnalda
   * eski holat" degan ziddiyatni ekranda qoldirardi.
   */
  payments: {
    all: ['payments'] as const,
    channels: ['payments', 'channels'] as const,
    detail: (id: string) => ['payments', 'detail', id] as const,
  },

  cashdesk: {
    all: ['cashdesk'] as const,
    branches: ['cashdesk', 'branches'] as const,
    expenseAccounts: (branchId: string) => ['cashdesk', 'expenseAccounts', branchId] as const,
    slipCompany: ['cashdesk', 'slipCompany'] as const,
    transferTotals: (filters: Readonly<Record<string, readonly string[]>>) =>
      ['cashdesk', 'transfers', 'totals', filters] as const,
    balance: (branchId: string | null) => ['cashdesk', 'balance', branchId] as const,
    period: (branchIds: readonly string[], from: string | null, to: string | null) =>
      ['cashdesk', 'period', [...branchIds].sort().join(','), from, to] as const,
    flow: (branchIds: readonly string[], from: string | null, to: string | null) =>
      ['cashdesk', 'flow', [...branchIds].sort().join(','), from, to] as const,
    history: (branchId: string, from: string, to: string) =>
      ['cashdesk', 'history', branchId, from, to] as const,
    receipts: ['cashdesk', 'receipts'] as const,
    myDay: (day: string) => ['cashdesk', 'receipts', 'mine', day] as const,
    transfers: ['cashdesk', 'transfers'] as const,
    transferTypes: ['cashdesk', 'transfer-types'] as const,
    entries: ['cashdesk', 'entries'] as const,
    attachments: (owner: string, id: string) => ['cashdesk', 'attachments', owner, id] as const,
    settings: ['cashdesk', 'settings'] as const,
    directory: (term: string) => ['cashdesk', 'directory', term] as const,
    quote: (subscriberId: string, planId: string, branchId: string | null) =>
      ['cashdesk', 'quote', subscriberId, planId, branchId] as const,
    discounts: ['cashdesk', 'discounts'] as const,
    approvedDiscounts: (subscriberId: string) => ['cashdesk', 'discounts', 'approved', subscriberId] as const,
    discountPolicies: ['cashdesk', 'discount-policies'] as const,
  },

  invoices: {
    all: ['invoices'] as const,
    list: ['invoices', 'list'] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
    billable: (subscriberId: string) => ['invoices', 'billable', subscriberId] as const,
  },

  paymentCategories: ['paymentCategories'] as const,
  branchOptions: ['branchOptions'] as const,

  financeDashboard: (filter: object) => ['financeDashboard', filter] as const,

  /** To'lov provayderi sozlamalari — `psp.read` / `psp.configure` */
  paymentProviders: {
    all: ['paymentProviders'] as const,
  },

  ledger: {
    accounts: ['ledger', 'accounts'] as const,
    entries: (accountId: string) => ['ledger', 'entries', accountId] as const,
    txn: (id: string) => ['ledger', 'txn', id] as const,
    reconciliation: ['ledger', 'reconciliation'] as const,
    /**
     * Tranzaksiyalar jadvali — BUTUN jurnal (`GET /api/ledger/entries`).
     *
     * ⚠️ `entries(accountId)` DAN ALOHIDA: u bitta hisobning ko'chirmasi,
     * bu esa barcha hisoblar bo'ylab filtrlanadigan ro'yxat. Bir kalit
     * ostida turishsa, hisob oynasini ochish tranzaksiyalar sahifasining
     * keshini o'chirib yuborardi.
     *
     * ⚠️ Filtr va davr kalitga QO'LDA qo'shilmaydi — `DataTable` jadval
     * holatini o'zi qo'shadi (`toQueryParams`), ya'ni eskirgan javob
     * yangisini almashtira olmaydi.
     */
    journal: ['ledger', 'journal'] as const,
  },

  venues: {
    all: ['venues'] as const,
    /** Holat filtri KALITGA kiradi — u o'zgarsa YANGI so'rov (`AuditPage` andozasi) */
    list: (state: string | null) => ['venues', 'list', state] as const,
    detail: (id: string) => ['venues', 'detail', id] as const,
    zones: (id: string) => ['venues', 'zones', id] as const,
    /** Mehmon portalining ko'rinishi — ob'ekt darajasida, zona emas */
    portalTemplate: (id: string) => ['venues', 'portal-template', id] as const,
  },

  journal: {
    /** Filtr obyekti kalitga kiradi — u o'zgarsa YANGI so'rov (`AuditPage` bilan bir xil andoza) */
    list: (filter: Readonly<Record<string, unknown>>) => ['journal', 'list', filter] as const,
    entry: (id: string) => ['journal', 'entry', id] as const,
    compliance: (venueId: string, from: string, to: string) =>
      ['journal', 'compliance', venueId, from, to] as const,
  },

  campaigns: {
    all: (venueId: string | null) => ['campaigns', 'all', venueId] as const,
    detail: (id: string) => ['campaigns', 'detail', id] as const,
    stats: (id: string, from: string, to: string) => ['campaigns', 'stats', id, from, to] as const,
  },

  notify: {
    /** Panel hammasini bir so'rovda oladi va `code`/`eventType` bo'yicha o'zi guruhlaydi */
    templates: ['notify', 'templates'] as const,
    subscriptions: ['notify', 'subscriptions'] as const,
    /** Sana oralig'i + filtr kalitga kiradi — `AuditPage` bilan bir xil andoza */
    messages: (filter: Readonly<Record<string, unknown>>) => ['notify', 'messages', filter] as const,
    deadLetters: ['notify', 'messages', 'dead'] as const,
    quietHours: ['notify', 'quietHours'] as const,
    telegramLink: (subscriberId: string) => ['notify', 'telegramLink', subscriberId] as const,
    whatsappSettings: ['notify', 'whatsappSettings'] as const,
  },

  vouchers: {
    batches: ['vouchers', 'batches'] as const,
    batch: (id: string) => ['vouchers', 'batch', id] as const,
    /** ⚠️ Kod — bir martalik sir (`voucherApi.ts`). Faqat `VoucherCodesDrawer` ochiq bo'lganda so'raladi. */
    codes: (batchId: string) => ['vouchers', 'codes', batchId] as const,
    activations: ['vouchers', 'activations'] as const,
    register: ['vouchers', 'register'] as const,
    /** Filtr kalitning oxiriga qo'shiladi — bekor qilish shu prefiks bilan */
    transfers: ['vouchers', 'transfers'] as const,
    vispReport: ['vouchers', 'vispReport'] as const,
  },
  ipPools: {
    all: ['ip-pools'] as const,
    /*
      ⚠️ Manzil KALITGA kiradi — boshqa IP qidirilganda YANGI so'rov
      ketishi kerak. Umumiy kalit bo'lsa eski natija ko'rinib turardi
      va operator boshqa abonentni ayblab qo'yishi mumkin edi.
    */
    byIp: (ip: string) => ['ip-pools', 'by-ip', ip] as const,
    history: (ip: string) => ['ip-pools', 'history', ip] as const,
    /*
      Egaga ajratilgan FAOL manzillar.

      ⚠️ Kalitda `ownerType` ham bor, `ownerId` yolg'iz emas. Backend
      ikkalasini birga so'raydi (`GET /api/inventory/ip-allocations/by-owner`)
      va turli reyestrlarning identifikatorlari bir-biriga to'qnashmasligiga
      kafolat yo'q — faqat `ownerId` ni kalit qilish ikki xil so'rovni
      bitta keshga tiqib qo'yardi.
    */
    byOwner: (ownerType: string, ownerId: string) =>
      ['ip-pools', 'by-owner', ownerType, ownerId] as const,
  },
  crm: {
    /*
      Filtr kalitning O'ZIDA — shunda filtr o'zgarganda ro'yxat qayta
      so'raladi va eski natija ko'rinib turmaydi.

      ⚠️ Lid telefonini ochish (`reveal`) uchun kalit ATAYLAB YO'Q: u
      bir martalik sir va keshga tushmasligi kerak (`crmApi.ts`).
    */
    tickets: (filter: unknown) => ['crm', 'tickets', filter] as const,
    ticket: (id: string) => ['crm', 'ticket', id] as const,
    leads: (filter: unknown) => ['crm', 'leads', filter] as const,
    interactions: (filter: unknown) => ['crm', 'interactions', filter] as const,
    /*
      `DataTable` uchun ASOS kalitlar — jadval holatini (sahifa, filtr,
      qidiruv) o'ZI qo'shadi, shuning uchun bu yerda filtr YO'Q.

      ⚠️ Prefiks yuqoridagilar bilan AYNAN bir xil (`['crm','tickets']`),
      ya'ni bitta `invalidateQueries` ikkalasini ham yangilaydi. Boshqa
      prefiks berilsa, jadval yangilanmay eski ro'yxatni ko'rsatib
      turardi — amal bajarilgani ekranda ko'rinmasdi.
    */
    ticketList: ['crm', 'tickets'] as const,
    leadList: ['crm', 'leads'] as const,
    interactionList: ['crm', 'interactions'] as const,
  },

  /**
   * Moliya (`xsib-finance`) — QARORLAR.md §18, §20.2.
   *
   * ⚠️ `ledger` DAN ALOHIDA PREFIKS va bu ATAYLAB. Ikkalasi ham "pul"
   * haqida, lekin ular IKKI HAR XIL HAQIQAT: `ledger` — jonli operatsion
   * jurnal (abonent balansi, agent depoziti), `finance` — yopilgan
   * davrlar va qo'lda kiritilgan xarajatlar. Bitta prefiksni baham
   * ko'rishsa, xarajat kiritish abonent balansi keshini ham bekor
   * qilardi va teskarisi — ya'ni ikki modul bir-birining so'rovlarini
   * behuda qayta ishga tushirardi (`sessions.onlineLogins` bilan bir xil
   * tuzoq, faqat teskari tomondan).
   *
   * ⚠️ `accounts` kalitida `activeOnly` BOR: hisoblar rejasi sahifasi
   * BARCHA hisoblarni so'raydi, xarajat oynasi esa faqat FAOLLARINI
   * (`AccountController#list` shu taqsimotni yozadi). Bitta kalitda
   * bo'lsa, qaysi so'rov oxirgi tugagani ekrandagi ro'yxatni belgilardi
   * — va o'chirilgan hisob xarajat oynasida tanlanadigan bo'lib
   * qolardi.
   */
  finance: {
    all: ['finance'] as const,
    accounts: (activeOnly: boolean) => ['finance', 'accounts', activeOnly] as const,
    costCenters: ['finance', 'costCenters'] as const,
    expenseCategories: ['finance', 'expenseCategories'] as const,
    /**
     * Filial jamlovchi hisoboti (`/api/finance/reports/branches`).
     * ⚠️ Sana oralig'i KALITDA — oldingi teng oyna bilan taqqoslash
     * shu oraliqqa bog'liq, oraliq almashsa YANGI so'rov kerak.
     */
    branchReport: (from: string, to: string) => ['finance', 'branchReport', from, to] as const,
    /**
     * Foyda hisoboti (`/api/finance/reports/income-statement`) va
     * aylanma-qoldiq vedomosti (`/trial-balance`).
     * ⚠️ Ikkalasi AYNI oynani oladi, lekin BOSHQA kalit ostida: bir
     * kalitda saqlansa, ikkinchi sahifa birinchisining javobini
     * ko'rsatib qo'yardi.
     */
    incomeStatement: (from: string, to: string, nodeId: string | null) =>
      ['finance', 'incomeStatement', from, to, nodeId] as const,
    trialBalance: (from: string, to: string, nodeId: string | null) =>
      ['finance', 'trialBalance', from, to, nodeId] as const,
    balanceSheet: (asOf: string, nodeId: string | null) =>
      ['finance', 'balanceSheet', asOf, nodeId] as const,
    cashFlow: (from: string, to: string, nodeId: string | null) =>
      ['finance', 'cashFlow', from, to, nodeId] as const,
    periods: ['finance', 'periods'] as const,
    /** Jurnal sahifasi; jadval holati (filtr, saralash, sahifa) kalitga `DataTable` da qo'shiladi */
    entries: ['finance', 'entries'] as const,
    entryTotals: (filters: Readonly<Record<string, readonly string[]>>, search: string) =>
      ['finance', 'entries', 'totals', filters, search] as const,
    entry: (id: string) => ['finance', 'entry', id] as const,
    expenseAttachments: (entryId: string) => ['finance', 'expenseAttachments', entryId] as const,
  },

  /** Yetkazib beruvchi va xarid (xsib-finance supplier paketi). Har amaldan keyin `supplier.all` bekor qilinadi. */
  supplier: {
    all: ['supplier'] as const,
    suppliers: ['supplier', 'suppliers'] as const,
    active: ['supplier', 'active'] as const,
    detail: (id: string) => ['supplier', 'detail', id] as const,
    summary: (id: string) => ['supplier', 'summary', id] as const,
    ledger: (id: string) => ['supplier', 'ledger', id] as const,
    attachments: (id: string) => ['supplier', 'attachments', id] as const,
    purchases: ['supplier', 'purchases'] as const,
    purchase: (id: string) => ['supplier', 'purchase', id] as const,
    receipts: (id: string) => ['supplier', 'receipts', id] as const,
    payments: (id: string) => ['supplier', 'payments', id] as const,
    receiving: ['supplier', 'receiving'] as const,
  },
  stakeholder: {
    all: ['stakeholder'] as const,
    list: (status: string) => ['stakeholder', 'list', status] as const,
    detail: (id: string) => ['stakeholder', 'detail', id] as const,
    shares: (id: string) => ['stakeholder', 'shares', id] as const,
    ledger: (id: string) => ['stakeholder', 'ledger', id] as const,
    distributions: (id: string) => ['stakeholder', 'distributions', id] as const,
    performance: (id: string) => ['stakeholder', 'performance', id] as const,
    attachments: (id: string) => ['stakeholder', 'attachments', id] as const,
    run: (periodId: string, orgNodeId: string | null) => ['stakeholder', 'run', periodId, orgNodeId] as const,
    paymentAccounts: ['stakeholder', 'paymentAccounts'] as const,
    adjustmentAccounts: ['stakeholder', 'adjustmentAccounts'] as const,
  },
} as const
